import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/pro-solid-svg-icons/faCopy';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';

import {
  Company,
  EmployerAdmin,
  EmployerAdminCreateResponse,
  EmployerAdminService,
} from 'us-web-services';
import { Table, TableInstance } from 'src/components/common/Table';
import PageStyles from 'src/styles/PageStyles';
import DateUtil from 'src/util/DateUtil';

interface Props {
  company: Company;
}

function CompanyEmployerAdmins(props: Props) {
  const { company } = props;
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [createResult, setCreateResult] = useState<EmployerAdminCreateResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState<EmployerAdmin | null>(null);

  const { classes } = PageStyles();
  const tableRef = React.useRef<TableInstance>();

  const refreshAdmins = () => tableRef.current?.fetchData();

  const handleAddAdmin = async () => {
    if (!email.trim()) {
      toast.error('Email is required');

      return;
    }

    try {
      const response = await EmployerAdminService.create({
        email: email.trim(),
        companyId: company.id,
      });

      setCreateResult(response.data.data);
      setCreateModalOpen(false);
      setSuccessModalOpen(true);
      setEmail('');
      refreshAdmins();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to create employer admin';

      toast.error(message);
    }
  };

  const handleDeleteClick = (admin: EmployerAdmin) => {
    setAdminToDelete(admin);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!adminToDelete?.id) return;

    try {
      await EmployerAdminService.delete(adminToDelete.id);
      toast.success('Admin deactivated successfully');
      setConfirmDeleteOpen(false);
      setAdminToDelete(null);
      refreshAdmins();
    } catch {
      toast.error('Failed to deactivate admin');
    }
  };

  const handleCopyPassword = async () => {
    if (!createResult?.generatedPassword) {
      return;
    }

    if (!navigator.clipboard) {
      toast.error('Clipboard not available');

      return;
    }

    try {
      await navigator.clipboard.writeText(createResult.generatedPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleCloseSuccessModal = () => {
    setSuccessModalOpen(false);
    setCreateResult(null);
    setCopied(false);
  };

  const COLUMNS = useMemo(
    () => [
      {
        id: 'employer-admins',
        header: 'Employer Portal Admins',
        columns: [
          {
            header: 'Email',
            accessorKey: 'email',
            size: 250,
          },
          {
            header: 'Name',
            accessorFn: (row: EmployerAdmin) =>
              [row.firstName, row.lastName].filter(Boolean).join(' ') || '—',
            size: 150,
          },
          {
            header: 'Status',
            accessorFn: (row: EmployerAdmin) => (row.active ? 'Active' : 'Inactive'),
            size: 100,
          },
          {
            header: 'Created',
            accessorFn: (row: EmployerAdmin) =>
              row.created ? DateUtil.getReadableDateTime(row.created) : '—',
            size: 150,
          },
        ],
      },
    ],
    [],
  );

  return (
    <Paper className={classes.paper}>
      <Typography variant='h6' gutterBottom>
        Employer Portal Admins
      </Typography>
      <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
        Manage administrators who can access the employer self-service portal for this company.
      </Typography>

      <Table
        ref={tableRef}
        columns={COLUMNS}
        retrieveData={EmployerAdminService.getByFilter}
        params={() => [
          {
            key: 'companyId',
            value: company.id.toString(),
          },
        ]}
        alwaysApplyParams
        addAction={() => setCreateModalOpen(true)}
        renderRowActionsCustom={({ row }) => (
          <Button
            color='error'
            size='small'
            onClick={() => handleDeleteClick(row.original)}
          >
            Deactivate
          </Button>
        )}
        hideSearchBar
      />

      <Dialog open={createModalOpen} onClose={() => setCreateModalOpen(false)}>
        <DialogTitle>Add Employer Admin</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            label='Email Address'
            type='email'
            fullWidth
            variant='outlined'
            value={email}
            onChange={e => setEmail(e.target.value)}
            sx={{ mt: 1, minWidth: 350 }}
          />
          <Typography variant='caption' color='textSecondary' sx={{ mt: 1, display: 'block' }}>
            If this email already exists as an UrbanSitter user, they will be linked to the
            employer portal using their existing password. Otherwise, a new account will be created
            with a generated password.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateModalOpen(false)}>Cancel</Button>
          <Button onClick={handleAddAdmin} variant='contained' color='primary'>
            Add Admin
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={successModalOpen} onClose={handleCloseSuccessModal} maxWidth='sm' fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FontAwesomeIcon icon={faCheckCircle} style={{ color: 'green' }} />
            Admin Created Successfully
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant='body1' sx={{ mb: 2 }}>
            <strong>Email:</strong> {createResult?.admin?.email}
          </Typography>

          {createResult?.linkedExistingUser ? (
            <Box
              sx={{
                p: 2,
                bgcolor: 'info.light',
                borderRadius: 1,
              }}
            >
              <Typography variant='body2'>
                This user already has an UrbanSitter account. They can log into the employer portal
                using their <strong>existing password</strong>.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                p: 2,
                bgcolor: 'warning.light',
                borderRadius: 1,
              }}
            >
              <Typography variant='body2' sx={{ mb: 1 }}>
                <strong>Generated Password:</strong>
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                }}
              >
                <Typography variant='body1' sx={{ fontFamily: 'monospace', flexGrow: 1 }}>
                  {createResult?.generatedPassword}
                </Typography>
                <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                  <IconButton onClick={handleCopyPassword} size='small'>
                    <FontAwesomeIcon icon={copied ? faCheckCircle : faCopy} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant='caption' color='textSecondary' sx={{ mt: 1, display: 'block' }}>
                Copy this password and share it securely with the admin. This is the only time it
                will be shown.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSuccessModal} variant='contained'>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Deactivate Admin</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to deactivate <strong>{adminToDelete?.email}</strong>?
          </Typography>
          <Typography variant='body2' color='textSecondary' sx={{ mt: 1 }}>
            They will no longer be able to access the employer portal.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant='contained' color='error'>
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default CompanyEmployerAdmins;
